import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { ITEM_TYPES, ASSET_CLASSES, ITEM_TYPE_OPTIONS, SUPPORTED_CURRENCIES } from '../constants';
import { processItemFormData, formatCurrency } from '../helpers';
import { Info, TestTube2, Calendar } from 'lucide-react';
import { calculateItemAnnualAppreciationValue } from '../logic/analytics';
import { calculateMonthlyPayment, debtBalanceAt, itemMonthlyCashflow } from '../logic/engine';
import InfoDisplay from './InfoDisplay';
import { CheckRow } from './Switch';

/**
 * Every item in the plan is edited here, on Variant C's form anatomy: a `.field`
 * is a label plus an `.input` box, and the box owns whatever else belongs to the
 * value — the currency or `%` unit as an `<i>`, a frequency segment welded to
 * the right edge, a calendar button.
 *
 * Two things deliberately diverge from the demo. The Monte Carlo fields keep the
 * app's violet separation (see `.mc-block` in overrides.css), and the switches
 * are real checkboxes rather than the demo's script-toggled spans.
 */

const symbolFor = (currency) =>
    (SUPPORTED_CURRENCIES.find(c => c.code === currency) || { symbol: currency }).symbol;

/**
 * A money field. `readOnly` makes it `.is-computed`: same shape as its
 * neighbours, dashed and unfocusable, because the app derives the number rather
 * than asking for it.
 */
const NumberInput = React.memo(function NumberInput({ label, name, value, onChange, placeholder, required = false, note, readOnly = false, currency = 'CZK' }) {
    return (
        <label className="field">
            <span>{label}</span>
            <div className={`input${readOnly ? ' is-computed' : ''}`}>
                <input
                    type="text"
                    inputMode="decimal"
                    name={name}
                    id={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    readOnly={readOnly}
                    tabIndex={readOnly ? -1 : undefined}
                />
                <i>{symbolFor(currency)}</i>
            </div>
            {note && <span className="field-hint">{note}</span>}
        </label>
    );
});

/**
 * A rate field. The suggestions sit inside the field they fill, each with the
 * info affordance that says where the number comes from — a chip that only
 * offers a value is a number with no provenance.
 */
const PercentageInput = React.memo(function PercentageInput({ label, name, value, onChange, placeholder, required = false, suggestions, theme }) {
    const { t } = useTranslation();
    const chips = Array.isArray(suggestions) ? suggestions : [];
    // a rate is a number: letters never reach the state
    const onNumber = (e) => { if (/^-?[0-9,.]*$/.test(e.target.value)) onChange(e); };

    return (
        <label className="field">
            <span>{label}</span>
            <div className="input">
                <input
                    type="text"
                    inputMode="decimal"
                    name={name}
                    id={name}
                    value={value}
                    onChange={onNumber}
                    placeholder={placeholder}
                    required={required}
                />
                <i>%</i>
            </div>
            {chips.length > 0 && (
                <div className="chips">
                    <span className="chips-label">{t('itemForm.suggestionsLabel')}</span>
                    {chips.map((suggestion, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => onChange({ target: { name, value: suggestion.value } })}
                            className="chip"
                            title={suggestion.display}
                        >
                            <span className="chip-text">{suggestion.display}</span>
                            {suggestion.tooltipKey && (
                                <InfoDisplay
                                    content={t(suggestion.tooltipKey)}
                                    theme={theme}
                                    drawerTitle={`${t('itemForm.suggestionsLabel')} ${suggestion.display}`}
                                >
                                    <span className="chip-info" aria-label={t('general.help')}>
                                        <Info size={13} />
                                    </span>
                                </InfoDisplay>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </label>
    );
});

/**
 * Two fields side by side — but only while there are two. Several pairs lose
 * their second half to the simulation block, and a `.row-2` with one child is a
 * half-width field beside a hole.
 */
const Pair = ({ children }) => {
    const kids = React.Children.toArray(children).filter(Boolean);
    return kids.length > 1 ? <div className="row-2">{kids}</div> : <>{kids}</>;
};

/** The three growth numbers, in the violet block that marks them as simulation. */
const SimulationFields = ({ baseFieldName, item, handleChange, label }) => {
    const { t } = useTranslation();
    const handleSimChange = (e) => {
        const { value } = e.target;
        if (value === '' || value === '-' || /^-?[0-9,.]*$/.test(value)) {
            handleChange(e);
        }
    };

    const bound = (suffix, label) => (
        <label className="field">
            <span>{label}</span>
            <div className="input">
                <input
                    type="text"
                    inputMode="decimal"
                    name={`${baseFieldName}_${suffix}`}
                    id={`${baseFieldName}_${suffix}`}
                    value={item[`${baseFieldName}_${suffix}`] || ''}
                    onChange={handleSimChange}
                />
                <i>%</i>
            </div>
        </label>
    );

    return (
        <div className="mc-block">
            {/* Annotation pass 2: the heading names the rate this range belongs
                to, not the mechanism. A rented property simulates two rates —
                appreciation and rent-and-costs growth — so the old fixed
                "Simulation Range" printed the same heading twice on one form
                with nothing to tell the pair apart. The label passed in is the
                same one the plain percentage field carries when simulation is
                off, so the two states name the value identically. The flask is
                the icon the enable switch above already uses. */}
            <span className="field-label mc-title">
                <TestTube2 aria-hidden="true" />
                {label}
            </span>
            <div className="row-3">
                {bound('min', t('itemForm.simulation.minGrowth'))}
                {bound('avg', t('itemForm.simulation.avgGrowth'))}
                {bound('max', t('itemForm.simulation.maxGrowth'))}
            </div>
        </div>
    );
};


const ItemForm = forwardRef(function ItemForm({ initialData, onSubmit, onCancel, showModal, theme, isMobile, isInsideModal = false, containerClass = '', currency }, ref) {
    const { t, i18n } = useTranslation();
    const [item, setItem] = useState(null);
    const [preview, setPreview] = useState({ netValue: 0, grossValue: 0, cashflow: 0, annualAppreciation: 0 });

    useEffect(() => {
        if(item) {
            const processed = processItemFormData(item);
            const cashflow = itemMonthlyCashflow(processed);
            const annualAppreciation = calculateItemAnnualAppreciationValue(processed);
            let netValue = 0;
            let grossValue = 0;
            switch(processed.type) {
                case ITEM_TYPES.REAL_ESTATE: 
                    grossValue = processed.currentValue; 
                    netValue = processed.currentValue - (processed.hasMortgage ? debtBalanceAt(processed) : 0); 
                    break;
                case ITEM_TYPES.OTHER_INVESTMENT: 
                    grossValue = processed.currentValue; 
                    netValue = processed.currentValue; 
                    break;
                case ITEM_TYPES.LOAN: 
                    grossValue = 0; 
                    netValue = -debtBalanceAt(processed); 
                    break;
                default: break;
            }
            setPreview({ netValue, grossValue, cashflow, annualAppreciation });
        }
    }, [item]);

    const formRef = React.useRef(null);
    // Editing rarely changes what a thing *is*, so the five-way picker waits
    // behind "Change type" instead of leading the form.
    const [showTypes, setShowTypes] = useState(!initialData);
    useEffect(() => { setShowTypes(!initialData); }, [initialData]);
    const mortgageStartDateRef = React.useRef(null);
    const loanStartDateRef = React.useRef(null);

    const openDatePicker = (ref) => {
        if (!ref || !ref.current) return;
        const input = ref.current;
        input.focus();
        if (typeof input.showPicker === 'function') {
            input.showPicker();
        }
    };
    
    useImperativeHandle(ref, () => ({ 
        getFormState: () => { return item; },
        validate: () => !formRef.current || formRef.current.reportValidity(),
        submit: () => {
            // D2 — the demo used to be refused here, two lines saying the same
            // thing. Submission now goes through to handleAddItem /
            // handleUpdateItem, which write to the demo's in-memory store.
            if (isInsideModal && onSubmit) {
                // the drawer's Save sits outside the <form>, so ask it to check itself:
                // the browser points at the first empty required field
                if (formRef.current && !formRef.current.reportValidity()) return;
                onSubmit(item);
            } else if (formRef.current) {
                formRef.current.requestSubmit();
            }
        }
    }));

    useEffect(() => {
        const defaultState = (data) => {
            const defaultType = ITEM_TYPES.REAL_ESTATE;
            let typeSpecificDefaults = {};
            const typeForDefaults = data?.type || defaultType;
            switch (typeForDefaults) {
                case ITEM_TYPES.REAL_ESTATE: typeSpecificDefaults = { currentValue: '', annualAppreciationRate: '', hasMortgage: false, mortgageInitialAmount: '', mortgageTermDisplay: '', mortgageTermUnit: 'years', mortgageMonthlyPayment: '', mortgageInterestRate: '', mortgageStartDate: new Date().toISOString().split('T')[0], isRented: false, monthlyRentalIncome: '', monthlyCosts: '', amountFrequency: 'monthly', rentalAndCostsGrowthRate: '', isSimulationEnabled: false, sim_annualAppreciationRate_min: '', sim_annualAppreciationRate_avg: '', sim_annualAppreciationRate_max: '', sim_rentalAndCostsGrowthRate_min: '', sim_rentalAndCostsGrowthRate_avg: '', sim_rentalAndCostsGrowthRate_max: '' }; break;
                case ITEM_TYPES.OTHER_INVESTMENT: typeSpecificDefaults = { currentValue: '', monthlyContribution: '', contributionFrequency: 'monthly', isGrowth: false, annualGrowthRate: '', isIncome: false, annualIncomeYield: '', assetClass: ASSET_CLASSES[0].value, isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
                case ITEM_TYPES.INCOME: typeSpecificDefaults = { monthlyIncome: '', amountFrequency: 'monthly', annualGrowthRate: '0', isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
                case ITEM_TYPES.EXPENSE: typeSpecificDefaults = { monthlyExpense: '', amountFrequency: 'monthly', annualGrowthRate: '0', isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
                case ITEM_TYPES.LOAN: typeSpecificDefaults = { loanAmount: '', loanTermDisplay: '', loanTermUnit: 'years', monthlyPayment: '', interestRate:'', startDate: new Date().toISOString().split('T')[0] }; break;
                default: break;
            }
            let processedData = data ? { ...data } : {};
            if (data) {
                ['annualAppreciationRate', 'annualGrowthRate', 'annualIncomeYield', 'interestRate', 'mortgageInterestRate', 'rentalAndCostsGrowthRate', 
                 'sim_annualAppreciationRate_min', 'sim_annualAppreciationRate_avg', 'sim_annualAppreciationRate_max',
                 'sim_rentalAndCostsGrowthRate_min', 'sim_rentalAndCostsGrowthRate_avg', 'sim_rentalAndCostsGrowthRate_max',
                 'sim_annualGrowthRate_min', 'sim_annualGrowthRate_avg', 'sim_annualGrowthRate_max'
                ].forEach(field => {
                    if(typeof processedData[field] === 'number'){ processedData[field] = parseFloat((processedData[field] * 100).toPrecision(15)).toString(); } else if(typeof processedData[field] !=='string'){ processedData[field]=''; }
                });
                if(processedData.type===ITEM_TYPES.LOAN&&processedData.loanTermTotalMonths){const unit=processedData.loanTermUnit||(processedData.loanTermTotalMonths>=12&&processedData.loanTermTotalMonths%12===0?'years':'months');processedData.loanTermDisplay=unit==='years'?processedData.loanTermTotalMonths/12:processedData.loanTermTotalMonths;processedData.loanTermUnit=unit;}
                if(processedData.type===ITEM_TYPES.REAL_ESTATE&&processedData.hasMortgage&&processedData.mortgageTermTotalMonths){const unit=processedData.mortgageTermUnit||(processedData.mortgageTermTotalMonths>=12&&processedData.mortgageTermTotalMonths%12===0?'years':'months');processedData.mortgageTermDisplay=unit==='years'?processedData.mortgageTermTotalMonths/12:processedData.mortgageTermTotalMonths;processedData.mortgageTermUnit=unit;}
                if (processedData.type === ITEM_TYPES.OTHER_INVESTMENT && !processedData.assetClass) { processedData.assetClass = ASSET_CLASSES[0].value; }
                if (processedData.note == null) { processedData.note = ''; }
            }
            return { name: '', note: data?.note ?? '', type: defaultType, ...typeSpecificDefaults, ...processedData, isActive: data?.isActive !== false };
        }
        setItem(defaultState(initialData));
    }, [initialData]);

    const calculatedPayment = useMemo(() => {
        if (!item) return null;
        if (item.type === ITEM_TYPES.LOAN) {
            const principal = parseFloat(String(item.loanAmount || '0').replace(',', '.'));
            const termMonths = item.loanTermUnit === 'years' ? parseFloat(item.loanTermDisplay || 0) * 12 : parseFloat(item.loanTermDisplay || 0);
            const annualRate = parseFloat(String(item.interestRate || '0').replace(',', '.')) / 100;
            if (principal > 0 && termMonths > 0 && annualRate >= 0) { return calculateMonthlyPayment(principal, annualRate, termMonths); }
        } else if (item.type === ITEM_TYPES.REAL_ESTATE && item.hasMortgage) {
            const principal = parseFloat(String(item.mortgageInitialAmount || '0').replace(',', '.'));
            const termMonths = item.mortgageTermUnit === 'years' ? parseFloat(item.mortgageTermDisplay || 0) * 12 : parseFloat(item.mortgageTermDisplay || 0);
            const annualRate = parseFloat(String(item.mortgageInterestRate || '0').replace(',', '.')) / 100;
            if (principal > 0 && termMonths > 0 && annualRate >= 0) { return calculateMonthlyPayment(principal, annualRate, termMonths); }
        }
        return null;
    }, [item]);

    useEffect(() => {
        if (calculatedPayment !== null && item) {
            const fieldToUpdate = item.type === ITEM_TYPES.LOAN ? 'monthlyPayment' : 'mortgageMonthlyPayment';
            const currentPayment = parseFloat(item[fieldToUpdate]);
            const newPayment = parseFloat(calculatedPayment.toFixed(0));
            if (currentPayment !== newPayment) {
                setItem(prev => ({ ...prev, [fieldToUpdate]: newPayment.toString() }));
            }
        }
    }, [calculatedPayment, item]);


    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        
        setItem(prev => {
            const newState = { ...prev, [name]: type === 'checkbox' ? checked : value };

            const syncAvgField = (rateField, avgField) => {
                if (newState.isSimulationEnabled && name === rateField) {
                    newState[avgField] = value;
                }
            };

            if (name === 'isSimulationEnabled' && checked) {
                if (newState.type === ITEM_TYPES.REAL_ESTATE) {
                    newState.sim_annualAppreciationRate_avg = newState.annualAppreciationRate;
                    newState.sim_rentalAndCostsGrowthRate_avg = newState.rentalAndCostsGrowthRate;
                } else if (newState.type === ITEM_TYPES.OTHER_INVESTMENT && newState.isGrowth) {
                    newState.sim_annualGrowthRate_avg = newState.annualGrowthRate;
                } else if (newState.type === ITEM_TYPES.INCOME || newState.type === ITEM_TYPES.EXPENSE) {
                     newState.sim_annualGrowthRate_avg = newState.annualGrowthRate;
                }
            } 
            
            syncAvgField('annualAppreciationRate', 'sim_annualAppreciationRate_avg');
            syncAvgField('rentalAndCostsGrowthRate', 'sim_rentalAndCostsGrowthRate_avg');
            syncAvgField('annualGrowthRate', 'sim_annualGrowthRate_avg');
            
            return newState;
        });
    }, []);

    const handleNumericChange = useCallback((e) => { const { name, value } = e.target; if (value === '' || value === '-' || /^-?[0-9,.]*$/.test(value)) setItem(prev => ({ ...prev, [name]: value })); }, []);

    // The frequency and term segments write the same shape a <select> did, so
    // handleChange stays the one place a field lands in state.
    const setField = useCallback((name, value) => {
        setItem(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const handleTypeChange = (newType) => {
        if (!item) return;
        const baseState = { name: item.name, note: item.note ?? '', type: newType, isActive: item.isActive };
        let newTypeDefaults = {};
        switch (newType) {
            case ITEM_TYPES.REAL_ESTATE: newTypeDefaults = { currentValue: '', annualAppreciationRate: '', hasMortgage: false, mortgageInitialAmount: '', mortgageTermDisplay: '', mortgageTermUnit: 'years', mortgageMonthlyPayment: '', mortgageInterestRate: '', mortgageStartDate: new Date().toISOString().split('T')[0], isRented: false, monthlyRentalIncome: '', monthlyCosts: '', amountFrequency: 'monthly', rentalAndCostsGrowthRate: '', isSimulationEnabled: false, sim_annualAppreciationRate_min: '', sim_annualAppreciationRate_avg: '', sim_annualAppreciationRate_max: '', sim_rentalAndCostsGrowthRate_min: '', sim_rentalAndCostsGrowthRate_avg: '', sim_rentalAndCostsGrowthRate_max: '' }; break;
            case ITEM_TYPES.OTHER_INVESTMENT: newTypeDefaults = { currentValue: '', monthlyContribution: '', contributionFrequency: 'monthly', isGrowth: false, annualGrowthRate: '', isIncome: false, annualIncomeYield: '', assetClass: ASSET_CLASSES[0].value, isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
            case ITEM_TYPES.INCOME: newTypeDefaults = { monthlyIncome: '', amountFrequency: 'monthly', annualGrowthRate: '0', isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
            case ITEM_TYPES.EXPENSE: newTypeDefaults = { monthlyExpense: '', amountFrequency: 'monthly', annualGrowthRate: '0', isSimulationEnabled: false, sim_annualGrowthRate_min: '', sim_annualGrowthRate_avg: '', sim_annualGrowthRate_max: '' }; break;
            case ITEM_TYPES.LOAN: newTypeDefaults = { loanAmount: '', loanTermDisplay: '', loanTermUnit: 'years', monthlyPayment: '', interestRate:'', startDate: new Date().toISOString().split('T')[0] }; break;
            default: break;
        }
        // Anything the new type also has (a current value, a growth rate, the
        // frequency) survives the switch; only what it can't use is dropped.
        const kept = Object.fromEntries(Object.keys(newTypeDefaults)
            .filter(k => item[k] !== undefined && item[k] !== '')
            .map(k => [k, item[k]]));
        setItem({ ...baseState, ...newTypeDefaults, ...kept });
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isInsideModal) { onSubmit(item); }
    };

    /**
     * A term: a number with its unit welded to the right edge, because a
     * two-year car loan and a thirty-year mortgage are the same field.
     */
    const renderTermInput = (valueName, unitName, label) => {
        const unit = item?.[unitName] || 'years';
        return (
            <label className="field">
                <span>{label}</span>
                <div className="input has-seg">
                    <input type="text" inputMode="numeric" name={valueName} id={valueName} value={item?.[valueName] || ''} onChange={handleNumericChange} required />
                    <span className="freq-seg" role="group" aria-label={t('itemForm.termUnit')}>
                        <button type="button" className={unit === 'years' ? 'on' : ''} onClick={() => setField(unitName, 'years')}>{t('general.years')}</button>
                        <button type="button" className={unit === 'months' ? 'on' : ''} onClick={() => setField(unitName, 'months')}>{t('general.months')}</button>
                    </span>
                </div>
            </label>
        );
    };

    /** An amount, with how often it happens attached to it and spelled out below. */
    const renderFrequencyInput = (amountName, frequencyName, label, required = false) => {
        const freq = item?.[frequencyName] || 'monthly';
        return (
            <label className="field">
                <span>{label}</span>
                <div className="input has-seg">
                    <input type="text" inputMode="decimal" name={amountName} id={amountName} value={item?.[amountName] || ''} onChange={handleNumericChange} required={required} />
                    <i>{symbolFor(currency)}</i>
                    <span className="freq-seg" role="group" aria-label={t('itemForm.howOften')}>
                        <button type="button" className={freq === 'monthly' ? 'on' : ''} onClick={() => setField(frequencyName, 'monthly')} title={t('general.monthly')}>{t('general.monthlyShort')}</button>
                        <button type="button" className={freq === 'annually' ? 'on' : ''} onClick={() => setField(frequencyName, 'annually')} title={t('general.annually')}>{t('general.annuallyShort')}</button>
                    </span>
                </div>
                <span className="field-hint">{freq === 'monthly' ? t('itemForm.perMonth') : t('itemForm.perYear')}</span>
            </label>
        );
    };

    /**
     * A date. The native picker indicator is tiny, and in Firefox absent, so the
     * app draws its own button and calls showPicker().
     */
    const renderDateInput = (name, label, inputRef, required = false) => (
        <label className="field">
            <span>{label}</span>
            <div className="input has-cal">
                <input
                    ref={inputRef}
                    type="date"
                    name={name}
                    id={name}
                    value={item?.[name] || new Date().toISOString().split('T')[0]}
                    onChange={handleChange}
                    required={required}
                />
                <button type="button" className="cal-btn" onClick={() => openDatePicker(inputRef)} aria-label={t('itemForm.openCalendar')}>
                    <Calendar size={17} />
                </button>
            </div>
        </label>
    );

    const incomeSuggestions = [ {value: "3.1", display: "3.1%", tooltipKey: "itemForm.inflationTooltip"}, {value: "6.49", display: "6.49%", tooltipKey: "itemForm.wageGrowthTooltip"} ];
    const expenseSuggestions = [ {value: "3.1", display: "3.1%", tooltipKey: "itemForm.inflationTooltip"} ];
    const realEstateSuggestions = [ {value: "7", display: "7%", tooltipKey: "itemForm.realEstateGrowthTooltip"} ];
    

    if (!item) return null;

    const finalContainerClass = isInsideModal ? '' : (containerClass || 'itemform-standalone');

    return (
        <div className={finalContainerClass}>
            {!isInsideModal && (
                <h2 className="itemform-title">{initialData ? t('itemForm.editItemTitle') : t('itemForm.addItemTitle')}</h2>
            )}
            <form ref={formRef} onSubmit={handleSubmit}>
                {/* The type is the form: picking one swaps the body below. Five
                    bodies, not one with everything on it. */}
                <span className="field-label">{t('itemForm.itemType')}</span>
                {!showTypes ? (
                    <div className="type-current">
                        {(() => {
                            const opt = ITEM_TYPE_OPTIONS.find(o => o.value === item.type);
                            return opt && (
                                <span className="type-opt on">
                                    {React.cloneElement(opt.icon, { size: 17 })}
                                    <span>{t(`itemForm.types.${opt.value}`)}</span>
                                </span>
                            );
                        })()}
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowTypes(true)}>{t('itemForm.changeType')}</button>
                    </div>
                ) : (
                <div className="type-picker">
                    {ITEM_TYPE_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleTypeChange(opt.value)}
                            className={`type-opt ${item.type === opt.value ? 'on' : ''}`}
                            aria-pressed={item.type === opt.value}
                        >
                            {React.cloneElement(opt.icon, { size: 19 })}
                            <span>{t(`itemForm.types.${opt.value}`)}</span>
                        </button>
                    ))}
                </div>
                )}

                <label className="field">
                    <span>{t('itemForm.itemName')}</span>
                    <div className="input">
                        <input type="text" name="name" id="name" value={item.name || ''} onChange={handleChange} required />
                    </div>
                </label>
                <label className="field">
                    <span>{t('itemForm.noteLabel')}</span>
                    <div className="input">
                        <textarea name="note" id="note" rows={2} value={item.note || ''} onChange={handleChange} placeholder={t('itemForm.notePlaceholder')} />
                    </div>
                </label>

                {item.type !== ITEM_TYPES.LOAN && (
                    <div className="mc-block">
                        <CheckRow
                            name="isSimulationEnabled"
                            checked={item.isSimulationEnabled || false}
                            onChange={handleChange}
                            icon={<TestTube2 />}
                        >
                            {t('itemForm.enableSimulation')}
                        </CheckRow>
                    </div>
                )}

                {item.type === ITEM_TYPES.REAL_ESTATE && (<>
                    <Pair>
                        <NumberInput label={t('itemForm.realEstate.currentValue')} name="currentValue" value={item.currentValue || ''} onChange={handleNumericChange} required currency={currency} />
                        {!item.isSimulationEnabled && <PercentageInput label={t('itemForm.realEstate.annualAppreciation')} name="annualAppreciationRate" value={item.annualAppreciationRate || ''} onChange={handleChange} required suggestions={realEstateSuggestions} theme={theme} />}
                    </Pair>
                    {item.isSimulationEnabled && <SimulationFields baseFieldName="sim_annualAppreciationRate" item={item} handleChange={handleChange} label={t('itemForm.realEstate.annualAppreciation')} />}

                    <CheckRow name="includeInFire" checked={item.includeInFire ?? true} onChange={handleChange} className="sep">
                        {t('itemForm.realEstate.includeInFire')}
                    </CheckRow>
                    <CheckRow name="hasMortgage" checked={item.hasMortgage || false} onChange={handleChange}>
                        {t('itemForm.realEstate.hasMortgage')}
                    </CheckRow>
                    {item.hasMortgage && (<>
                        <Pair>
                            <NumberInput label={t('itemForm.realEstate.mortgageInitialAmount')} name="mortgageInitialAmount" value={item.mortgageInitialAmount || ''} onChange={handleNumericChange} required currency={currency} />
                            <PercentageInput label={t('itemForm.realEstate.mortgageInterestRate')} name="mortgageInterestRate" value={item.mortgageInterestRate || ''} onChange={handleChange} required theme={theme} />
                        </Pair>
                        <Pair>
                            {renderTermInput('mortgageTermDisplay', 'mortgageTermUnit', t('itemForm.realEstate.mortgageTerm'))}
                            {renderDateInput('mortgageStartDate', t('itemForm.realEstate.mortgageStartDate'), mortgageStartDateRef, true)}
                        </Pair>
                        {/* The engine reads mortgageMonthlyPayment for a property and
                            monthlyPayment for a loan, and the effect above writes to
                            whichever the type calls for. This field used to read
                            monthlyPayment in both cases, so on a property it showed
                            nothing however much the app had computed. */}
                        <NumberInput label={t('itemForm.realEstate.mortgageMonthlyPayment')} name="mortgageMonthlyPayment" value={item.mortgageMonthlyPayment || ''} onChange={()=>{}} readOnly note={t('itemForm.computedHint')} currency={currency} />
                    </>)}

                    <CheckRow name="isRented" checked={item.isRented || false} onChange={handleChange} className="sep">
                        {t('itemForm.realEstate.isRented')}
                    </CheckRow>
                    <Pair>
                        {item.isRented ? renderFrequencyInput('monthlyRentalIncome', 'amountFrequency', t('itemForm.realEstate.rentalIncome'), true) : renderFrequencyInput('monthlyCosts', 'amountFrequency', t('itemForm.realEstate.costsWhenNotRented'), false)}
                        {!item.isSimulationEnabled && <PercentageInput label={t('itemForm.realEstate.rentalAndCostsGrowth')} name="rentalAndCostsGrowthRate" value={item.rentalAndCostsGrowthRate || ''} onChange={handleChange} suggestions={expenseSuggestions} theme={theme} />}
                    </Pair>
                    {item.isSimulationEnabled && <SimulationFields baseFieldName="sim_rentalAndCostsGrowthRate" item={item} handleChange={handleChange} label={t('itemForm.realEstate.rentalAndCostsGrowth')} />}
                    {item.isRented && (
                        <CheckRow name="isPassiveIncome" checked={item.isPassiveIncome ?? true} onChange={handleChange}>
                            {t('itemForm.realEstate.isPassiveIncome')}
                        </CheckRow>
                    )}
                </>)}

                {item.type === ITEM_TYPES.OTHER_INVESTMENT && (<>
                    <Pair>
                        <NumberInput label={t('itemForm.otherInvestment.currentValue')} name="currentValue" value={item.currentValue || ''} onChange={handleNumericChange} required currency={currency} />
                        <label className="field">
                            <span>{t('itemForm.otherInvestment.assetClass')}</span>
                            <select name="assetClass" id="assetClass" value={item.assetClass || ASSET_CLASSES[0].value} onChange={handleChange} required className="select">
                                {ASSET_CLASSES.map(ac => <option key={ac.value} value={ac.value}>{t(`assetClasses.${ac.value}`)}</option>)}
                            </select>
                        </label>
                    </Pair>
                    {renderFrequencyInput('monthlyContribution', 'contributionFrequency', t('itemForm.otherInvestment.contribution'))}

                    <CheckRow name="isGrowth" checked={item.isGrowth || false} onChange={handleChange} className="sep">
                        {t('itemForm.otherInvestment.isGrowth')}
                    </CheckRow>
                    {item.isGrowth && (item.isSimulationEnabled
                        ? <SimulationFields baseFieldName="sim_annualGrowthRate" item={item} handleChange={handleChange} label={t('itemForm.otherInvestment.annualGrowth')} />
                        : <PercentageInput label={t('itemForm.otherInvestment.annualGrowth')} name="annualGrowthRate" value={item.annualGrowthRate || ''} onChange={handleChange} required suggestions={realEstateSuggestions} theme={theme} />)}

                    <CheckRow name="isIncome" checked={item.isIncome || false} onChange={handleChange}>
                        {t('itemForm.otherInvestment.isIncome')}
                    </CheckRow>
                    {item.isIncome && <PercentageInput label={t('itemForm.otherInvestment.annualIncomeYield')} name="annualIncomeYield" value={item.annualIncomeYield || ''} onChange={handleChange} required theme={theme} />}
                    {item.isIncome && (
                        <CheckRow name="isPassiveIncome" checked={item.isPassiveIncome ?? true} onChange={handleChange}>
                            {t('itemForm.otherInvestment.isPassiveIncome')}
                        </CheckRow>
                    )}
                    <CheckRow name="includeInFire" checked={item.includeInFire ?? true} onChange={handleChange} className="sep">
                        {t('itemForm.otherInvestment.includeInFire')}
                    </CheckRow>
                </>)}

                {item.type === ITEM_TYPES.INCOME && (<>
                    {renderFrequencyInput('monthlyIncome', 'amountFrequency', t('itemForm.income.amount'), true)}
                    {item.isSimulationEnabled
                        ? <SimulationFields baseFieldName="sim_annualGrowthRate" item={item} handleChange={handleChange} label={t('itemForm.income.annualGrowth')} />
                        : <PercentageInput label={t('itemForm.income.annualGrowth')} name="annualGrowthRate" value={item.annualGrowthRate || ''} onChange={handleChange} suggestions={incomeSuggestions} theme={theme} />}
                    <CheckRow name="isPassiveIncome" checked={item.isPassiveIncome ?? false} onChange={handleChange} className="sep">
                        {t('itemForm.income.isPassiveIncome')}
                    </CheckRow>
                </>)}

                {item.type === ITEM_TYPES.EXPENSE && (<>
                    {renderFrequencyInput('monthlyExpense', 'amountFrequency', t('itemForm.expense.amount'), true)}
                    {item.isSimulationEnabled
                        ? <SimulationFields baseFieldName="sim_annualGrowthRate" item={item} handleChange={handleChange} label={t('itemForm.expense.annualGrowth')} />
                        : <PercentageInput label={t('itemForm.expense.annualGrowth')} name="annualGrowthRate" value={item.annualGrowthRate || ''} onChange={handleChange} suggestions={expenseSuggestions} theme={theme} />}
                </>)}

                {item.type === ITEM_TYPES.LOAN && (<>
                    <Pair>
                        <NumberInput label={t('itemForm.loan.loanAmount')} name="loanAmount" value={item.loanAmount || ''} onChange={handleNumericChange} required currency={currency} />
                        <PercentageInput label={t('itemForm.loan.interestRate')} name="interestRate" value={item.interestRate || ''} onChange={handleChange} required theme={theme} />
                    </Pair>
                    <Pair>
                        {renderTermInput('loanTermDisplay', 'loanTermUnit', t('itemForm.loan.term'))}
                        {renderDateInput('startDate', t('itemForm.loan.startDate'), loanStartDateRef, true)}
                    </Pair>
                    <NumberInput label={t('itemForm.loan.monthlyPayment')} name="monthlyPayment" value={item.monthlyPayment || ''} onChange={()=>{}} readOnly note={t('itemForm.computedHint')} currency={currency} />
                </>)}

                {/* What this item does to the plan, recomputed as you type: the
                    consequence belongs in the form, not behind a save. */}
                <div className="form-preview">
                    <span className="form-preview-label">{t('itemForm.financialPreview')}</span>
                    <div className="form-preview-figs">
                        <span>
                            <em>{t('dashboard.netWorth')}</em>
                            <b className={preview.netValue >= 0 ? 'pos' : undefined}>{formatCurrency(preview.netValue, i18n.language, currency)}</b>
                        </span>
                        <span>
                            <em>{t('portfolio.card.monthlyCashflow')}</em>
                            <b className={preview.cashflow >= 0 ? 'pos' : undefined}>{formatCurrency(preview.cashflow, i18n.language, currency)}</b>
                        </span>
                        <span>
                            <em>{t('portfolio.card.annualAppreciationAbs')}</em>
                            <b>{formatCurrency(preview.annualAppreciation, i18n.language, currency)}</b>
                        </span>
                    </div>
                </div>

                {!isInsideModal && (
                    <div className="itemform-foot">
                        <button type="button" onClick={onCancel} className="btn btn-ghost">{t('general.cancel')}</button>
                        <button type="submit" className="btn btn-grad">{initialData ? t('general.save') : t('general.add')}</button>
                    </div>
                )}
            </form>
        </div>
    );
});

export default ItemForm;
